import * as React from 'react';
import { FocusScope, useInteractOutside, usePreventScroll } from 'react-aria';

import { type IconDefinition, LoadingPanel, renderIcon } from '@gitbook/spine-react';

import { PortalAbsolute } from '../Portal';

export interface SearchModalProps {
    /** The value of the search query. */
    query: string;

    /** Placeholder for the search input */
    queryPlaceholder?: string;

    /** Callback when the search query is changed. */
    onQueryChange: (query: string) => void;

    /** Callback when the user is submitting the query by pressing enter. */
    onSubmitQuery?: (query: string) => void;

    /** Callback when the user is closing the modal. */
    onClose: () => void;

    /** Inner body */
    children: React.ReactNode;

    /** Layout elements */
    inputIcon?: IconDefinition;
    leftButton?: React.ReactNode;
    rightButton?: React.ReactNode;
    footer?: React.ReactNode;
}

export interface SearchModalRef {
    focus(): void;
    select(): void;
}

/**
 * Body of the search modal. Split in a different component so that the useEffect are called after the mount in the portal element.
 */
export const SearchModal = React.forwardRef(function SearchModal(
    props: SearchModalProps,
    ref: React.Ref<SearchModalRef>
): React.ReactElement {
    const {
        query,
        onQueryChange,
        onSubmitQuery,
        queryPlaceholder = 'Search…',
        onClose,
        children,
        footer,
        inputIcon: InputIconProp,
        leftButton,
        rightButton,
    } = props;

    const inputRef = React.useRef<HTMLInputElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const backdropRef = React.useRef<HTMLDivElement>(null);
    usePreventScroll();
    useInteractOutside({
        ref: contentRef,
        onInteractOutside: (event) => {
            // We only want to close the modal if the interaction outside is on its
            // backdrop. In other words, the event is triggered from within the dialog
            // but not within the dialog's content.
            //
            // We are ignoring interactions outside the dialog element because that
            // can include interactions in portals - such as palette menus.
            if (
                event.target instanceof Element &&
                backdropRef.current &&
                (backdropRef.current === event.target || backdropRef.current.contains(event.target))
            ) {
                onClose();
            }
        },
    });

    React.useImperativeHandle(ref, () => ({
        focus: () => {
            inputRef.current?.focus();
        },
        select: () => {
            inputRef.current?.select();
        },
    }));

    /**
     * Focus the input when the search view is mounted and select the entire text.
     */
    React.useEffect(() => {
        inputRef.current?.select();
    }, []);

    return (
        <PortalAbsolute>
            <div
                ref={backdropRef}
                className="fade-in fixed inset-0 z-[--z-index-popover] flex animate-in flex-col items-center bg-[--color-backdrop-bg] p-4 md:pt-[8vh] md:pb-[8vh]"
                style={{
                    backdropFilter: 'blur(5px)',
                }}
            >
                <FocusScope autoFocus restoreFocus contain>
                    <div
                        ref={contentRef}
                        className="fade-in zoom-in-90 mx-auto flex max-h-full w-full max-w-3xl animate-in flex-col overflow-hidden rounded-lg border bg-base shadow-elevated"
                    >
                        <div className="flex items-center px-6">
                            {leftButton}
                            {InputIconProp
                                ? renderIcon(InputIconProp, { size: 18, className: 'text-primary' })
                                : null}
                            <div className="flex1 flex w-full">
                                <input
                                    ref={inputRef}
                                    value={query}
                                    placeholder={queryPlaceholder}
                                    onChange={(event) => {
                                        onQueryChange(event.currentTarget.value);
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Escape') {
                                            onClose();
                                            return;
                                        }
                                        if (event.key === 'Enter') {
                                            onSubmitQuery?.(query);
                                            return;
                                        }
                                    }}
                                    className="flex-1 border-none bg-base p-4 text-base text-ui-base placeholder:text-muted"
                                />
                            </div>
                            {rightButton}
                        </div>
                        <div className="flex flex-1 flex-col overflow-y-auto">
                            <React.Suspense fallback={<LoadingPanel size="large" />}>
                                {children}
                            </React.Suspense>
                        </div>
                        {footer ? footer : null}
                    </div>
                </FocusScope>
            </div>
        </PortalAbsolute>
    );
});
